import React from 'react'
import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { ChevronUpDownIcon, LinkIcon, PlusIcon, QuestionMarkCircleIcon } from '@heroicons/react/20/solid'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { MuiPicker } from '../../MuiPicker'
import CalendarTeacherDropdown from '../CalendarTeacherDropdown/CalendarTeacherDropdown'
import axios from 'axios'
import { baseUrl } from '../../assets/Animations'
import { format, parseISO, startOfMonth, setDay, addWeeks, getDay, getWeekOfMonth, isValid } from 'date-fns'
import SwitchComponent from '../../assets/SwitchComponent'
import OfferOrEvent from '../CalendarEvent/OfferOrEvent'

let myToken = localStorage.getItem('token')
let myHeader = {
    headers: { Authorization: `Bearer ${myToken}` }
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const NewCalendarEvent = (props) => {
  const eventIsIntro = Boolean(props.newEvent?.isIntro)
  const [introLesson, setIntroLesson] = React.useState(
    () => eventIsIntro
  )
  const [somethingWasChanged, setSomethingWasChanged] = React.useState(false)
  const [attemptedSave, setAttemptedSave] = React.useState(false)
  const familyAddress = props.newEvent?.familyAddress
  const safeFamilyAddress = familyAddress && typeof familyAddress === 'object'
    ? familyAddress
    : {}
  const addressLine1 = safeFamilyAddress.addressLine1
    || safeFamilyAddress.AddressLine1
    || safeFamilyAddress.StreetAddress
    || safeFamilyAddress.streetAddress
    || ''
  const addressCity = safeFamilyAddress.city || safeFamilyAddress.City || ''
  const addressState = safeFamilyAddress.state || safeFamilyAddress.State || ''
  const addressZipcode = safeFamilyAddress.zipcode
    || safeFamilyAddress.zipCode
    || safeFamilyAddress.Zipcode
    || safeFamilyAddress.Zip
    || ''
  const hasFamilyAddress = Boolean(addressLine1 || addressCity || addressState || addressZipcode)
  const hasSelectedStudent = Boolean(props.newEvent?.studentId)

  React.useEffect(() => {
    if (!props.openEvent) {
      setSomethingWasChanged(false)
      setAttemptedSave(false)
      setIntroLesson(false)
      return
    }
    setIntroLesson(eventIsIntro)
  }, [eventIsIntro, props.openEvent])

  React.useEffect(() => {
    if(props.openEvent) {
      axios.get(`${baseUrl}/school-management/school/${props.schoolCalendar.schoolAbbreviation}`, myHeader)
      .then((res) => { props.setSchoolFamilies(res.data) })
      .catch((err) => console.log(err))
    }
  }, [props.openEvent])

  React.useEffect(() => {
    if(props.newEvent) {
      props.setNewEvent((prev) => ({...prev, isIntro: introLesson}))
    }
  }, [introLesson])


  const calculateAge = (birthday) => { // birthday is a date
    let fixedBDay = new Date(birthday)
     let ageDifMs = Date.now() - fixedBDay.getTime();
     let ageDate = new Date(ageDifMs); // miliseconds from epoch
     if(isNaN(Math.abs(ageDate.getUTCFullYear() - 1970))) {
      return 'Unknown '
     } else {
      return Math.abs(ageDate.getUTCFullYear() - 1970);
     }
  }

  const flipTheSwitch = (e) => {
    setSomethingWasChanged(true)
    setIntroLesson(e)
  }

  const removeStudent = () => {
    setSomethingWasChanged(true)
    props.setNewEvent((prev) => ({...prev, familyId: '', familyName: '', familyAddress: {}, studentId: '', familyPhoneNumber: '', studentDOB: '', parentName: '', studentName: '', waveCustomerId: '', familyEmail: ''}))
  }

  const changeRepeatStatus = (value) => {
    setSomethingWasChanged(true)
    if(value === 'none') {
      props.setNewEvent((prev) => ({...prev, recurring:{isOn: false, frequency: value, interval: 0}}))
    } else {
      props.setNewEvent((prev) => ({...prev, recurring:{isOn: true, frequency: value, interval: 0}}))
    }
  }

  const changeLessonLength = (value) => {
    setSomethingWasChanged(true)
    props.setNewEvent((prev) => ({...prev, lessonLength: value}))
  }

  const tryToDiscard = () => {
    if(somethingWasChanged) {
      props.setDiscardUnsavedChangesPopup(true)
      props.setDiscardType('new-event')
    } else {
      props.setOpenEvent(false)
      props.setNewEvent(false)
    }
  }

  const openTheFamilySearch = () => {
    props.setFamilySearchOpen(true)
    setSomethingWasChanged(true)
  }

  const changePendingStatus = (bool) => {
    setSomethingWasChanged(true)
    props.setNewEvent((prev) => ({...prev, isPendingOffer: bool}))
  }

  const tryToSaveTheEvent = () => {
    setAttemptedSave(true)
    if(props.newEvent.familyId !== '' && hasSelectedStudent && props.newEvent.teacherId !== '') {
      props.createCalendarEvent(props.newEvent)
    }
  }

  return (
    <Transition.Root show={props.openEvent} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={tryToDiscard}>
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <form className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                    <div className="h-0 flex-1 overflow-y-auto">
                      <div className={classNames(props.newEvent.isPendingOffer ? 'bg-purple-700' : 'bg-blue-700', "py-6 px-4 sm:px-6")}>
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg m-0 font-medium text-white"><input placeholder="New Event" value={props.newEvent.name} onChange={(e) => props.setNewEvent({...props.newEvent, name: e.target.value})} className={classNames(props.newEvent.isPendingOffer ? 'bg-purple-700' : 'bg-blue-700', "text-xl m-0 font-medium text-white border-none placeholder:text-white focus:ring-0 focus:outline-none")} /></Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className={classNames(props.newEvent.isPendingOffer ? 'bg-purple-700' : 'bg-blue-700', "rounded-md text-white border-none hover:text-white focus:outline-none focus:ring-0")}
                              onClick={() => tryToDiscard()}
                            >
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-1 flex-col justify-between">
                        <div className="divide-y divide-gray-200 px-4 sm:px-6">
                          <div className='text-right mt-2'>
                              <OfferOrEvent isPendingOffer={props.newEvent.isPendingOffer} changePendingStatus={changePendingStatus} />
                          </div>
                          <div className="space-y-6 pt-1 pb-5">
                            <div>
                            <div>
                              <CalendarTeacherDropdown teachers={props.teachers} newEvent={props.newEvent} setNewEvent={props.setNewEvent} setSomethingWasChanged={setSomethingWasChanged} attemptedSave={attemptedSave} />
                            </div>
                            <div className="block text-sm font-medium mt-5 mb-1 text-gray-700">Student:</div>
                              <div className={classNames((attemptedSave && !hasSelectedStudent) ? 'border-red-500 border-2 text-red-500' : 'border-gray-300 border text-black', 'p-2 border-solid shadow-sm rounded-md')}>
                                {!hasSelectedStudent
                                ? 
                                  <div className="text-left relative" onClick={() => openTheFamilySearch()}>
                                    <div className={'text-sm inline-block py-0 px-3 rounded-md cursor-pointer'}>
                                      {props.newEvent.familyId
                                        ? `Select a student from ${props.newEvent.familyName} family`
                                        : 'Select Student'}
                                    </div>
                                    <span className='right-0 absolute'>
                                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /> 
                                    </span>
                                  </div> 
                                :
                                <div>
                                  <div className='text-right'>
                                    <div onClick={() => removeStudent()} className='inline-block cursor-pointer text-center align-top font-sm text-xs text-gray-400 hover:text-gray-500 hover:text-white'>remove</div>
                                  </div>
                                  <div className='mt-3'>
                                    <label htmlFor="project-name" className="text-sm w-16 font-medium text-gray-500 inline-block">
                                      Student:
                                    </label>
                                    <div className='text-left pl-1 -mt-5 inline-block'>
                                      {props.newEvent && <div className=' font-bold'>{props.newEvent.studentName} {props.newEvent.familyName}</div>}
                                    </div>
                                  </div>
                                  <div className='mt-3'>
                                    <label htmlFor="project-name" className="inline-block text-sm mt-3 w-16 font-medium text-gray-500">
                                      Age:
                                    </label>
                                    <div className='inline-block ml-1'>
                                    {calculateAge(props.newEvent.studentDOB)} yrs old
                                    </div>
                                  </div>
                                  <div className='mt-3'>
                                  <label htmlFor="project-name" className="inline-block align-top text-sm mt-3 w-16 font-medium text-gray-500">
                                    Address:
                                  </label>
                                  <div className='text-left inline-block pl-5 mt-0'>
                                    {hasFamilyAddress
                                      ? <>
                                        <div className=' text-sm pl-0 mt-1'>{addressLine1}</div>
                                        <div className=' text-sm pl-0'>{addressCity}{addressCity && addressState ? ',' : ''} {addressState}</div>
                                        <div className=' text-sm pl-0'>{addressZipcode}</div>
                                      </>
                                      : <div className='text-sm pl-0 mt-1 text-gray-500'>Address unavailable</div>}
                                  </div>
                                  </div>
                                  <div>
                                  <div className='mt-3'>
                                    <label htmlFor="project-name" className="inline-block text-sm mt-3 w-16 font-medium text-gray-500">
                                      Parent
                                    </label>
                                    <div className='text-left pl-5 inline-block'>
                                      {props.newEvent && <div className=' font-bold'>{props.newEvent.parentName} {props.newEvent.familyName}</div>}
                                    </div>
                                  </div>
                                  <div className='mt-3'>
                                    <label htmlFor="project-name" className="inline-block text-sm mt-3 w-16 font-medium text-gray-500">
                                      Phone Number:
                                    </label>
                                    <div className='text-left pl-5 inline-block'>
                                      {props.newEvent && <div className=' font-bold'>{props.newEvent.familyPhoneNumber}</div>}
                                    </div>
                                  </div>
                                  <div className='mt-3'>
                                    <label htmlFor="project-name" className="inline-block text-sm mt-3 w-16 font-medium text-gray-500">
                                      Email:
                                    </label>
                                    <div className='text-left pl-5 inline-block'>
                                      {props.newEvent && <div className=' font-bold'>{props.newEvent.familyEmail}</div>}
                                    </div>
                                  </div>
                                </div>
                                </div>
                                }
                              </div>
                            </div>
                            <div className='text-left'>
                              <div className='text-sm text-gray-500'>Lesson Length:</div>
                              {!props.newEvent.isPendingOffer 
                              ?
                            <select className='w-full p-2 rounded-md border-gray-300 ' onChange={(e) => changeLessonLength(e.target.value)} value={props.newEvent.lessonLength}>
                              <option value={15}>15 minutes</option>
                              <option value={30}>30 minutes</option>
                              <option value={45}>45 minutes</option>
                              <option value={60}>60 minutes</option>
                            </select>
                            :
                            <select className='w-full p-2 rounded-md border-gray-300 ' onChange={(e) => changeLessonLength(e.target.value)} value={props.newEvent.lessonLength}>
                                  <option value={30}>30 minutes</option>
                                  <option value={60}>1 hour</option>
                                  <option value={90}>1.5 hours</option>
                                  <option value={120}>2 hours</option>
                                  <option value={150}>2.5 hours</option>
                                  <option value={180}>3 hours</option>
                                  <option value={210}>3.5 hours</option>
                                  <option value={240}>4 hours</option>
                            </select>
                            }
                            </div>
                            <div className='inline-block mt-5'>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <MuiPicker setNewEvent={props.setNewEvent} newEvent={props.newEvent} setSomethingWasChanged={setSomethingWasChanged} />
                              </LocalizationProvider>
                            </div>
                            {!props.newEvent.isPendingOffer && 
                              <div className='text-center inline-block m-10 align-top'>
                                <SwitchComponent enabled={introLesson} setEnabled={flipTheSwitch} />
                                <div className='text-xs'>Intro Lesson</div>
                              </div>
                            }
                            {props.newEvent.dateTime !== null && isValid(new Date(props.newEvent.dateTime)) && !props.newEvent.isPendingOffer &&
                              <div>
                                <select className='text-sm p-2 border-gray-300 rounded-md w-full' onChange={(e) => changeRepeatStatus(e.target.value)}>
                                  <option value="none">Does not Repeat</option>
                                  <option value="weekly">Weekly on {new Date(props.newEvent.dateTime) && format(new Date(props.newEvent.dateTime), 'EEEE')}</option>
                                  <option value="every-other-week">Every Other {new Date(props.newEvent.dateTime) && format(new Date(props.newEvent.dateTime), 'EEEE')}</option>
                                </select>
                              </div>
                            }
                            <div className="mt-4 flex text-xs">
                              <div className="ml-2 text-gray-400">Created by {props.userData.Name.split(' ')[0]}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 justify-end px-4 py-4">
                      <button
                        type="button"
                        className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer"
                        onClick={() => tryToDiscard()}
                      >
                        Cancel
                      </button>
                      <div
                        type="submit"
                        onClick={() => tryToSaveTheEvent()}
                        className="ml-4 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer"
                      >
                        Save
                      </div>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
export default NewCalendarEvent;
